import {Injectable} from '@angular/core';
import * as _ from 'lodash-es';
import {NEVER, Observable, Subject, Subscriber, TeardownLogic} from 'rxjs';
import {share, startWith} from 'rxjs/operators';
import {WidgetLinkingGroupId} from '../../enums/widget-linking-group';
import {WidgetSettings} from '../layout/widget/widget-settings';

interface Group {
    subject$: Subject<Partial<WidgetSettings>>;
    observable$: Observable<Partial<WidgetSettings>>;
    settings: Partial<WidgetSettings>;
}

interface GroupsDict {
    [groupId: number]: Group;
}

interface TabsDict {
    [tabId: string]: GroupsDict;
}

/**
 * The flag tells that the settings update event came from group, but not by the widget's settings direct update.
 * Usage: as a flag in widgetImplementation.setSettings / updateSettings.
 * @type {string}
 */
export const GROUP_SETTINGS_UPDATE_FLAG = 'groupSettings';

@Injectable()
export class WidgetLinkingGroupService {

    private tabs: TabsDict = {};

    public getLinkingGroupSettingsStream(
        groupId: WidgetLinkingGroupId,
        tabId: string,
        newSettings: Partial<WidgetSettings>
    ): Observable<Partial<WidgetSettings>> {
        let stream$: Observable<Partial<WidgetSettings>> = NEVER;

        if (groupId) {
            let group: Group = this.tabs[tabId] && this.tabs[tabId][groupId];

            if (group) {
                const groupSettings: Partial<WidgetSettings> = group.settings;

                if (!_.isEmpty(newSettings)) {
                    _.defaults(groupSettings, newSettings);
                }

                stream$ = group.observable$.pipe(
                    startWith(groupSettings)
                );
                group.subject$.next(groupSettings);
            } else {
                group = this.createGroup(groupId, tabId, newSettings);
                stream$ = group.observable$;
            }
        }

        return stream$;
    }

    public setLinkingGroupSettings(
        groupId: WidgetLinkingGroupId = WidgetLinkingGroupId.ALL,
        tabId: string,
        settings: Partial<WidgetSettings>
    ): void {
        groupId = groupId || WidgetLinkingGroupId.ALL;

        const group: Group = groupId && this.tabs[tabId] && this.tabs[tabId][groupId];
        if (group) {
            group.subject$.next(settings);

            if (groupId !== WidgetLinkingGroupId.ALL) {
                this.setLinkingGroupSettings(WidgetLinkingGroupId.ALL, tabId, settings);
            }
        }
    }

    private createGroup(groupId: WidgetLinkingGroupId, tabId: string, settings: Partial<WidgetSettings>): Group {
        const subject$ = new Subject<Partial<WidgetSettings>>();
        const observable$ = new Observable((subscriber: Subscriber<Partial<WidgetSettings>>): TeardownLogic => {
            subject$.subscribe(subscriber);

            return () => {
                subject$.complete();
                delete this.tabs[tabId][groupId];
            };
        }).pipe(
            share()
        );

        const group: Group = {subject$, observable$, settings};

        subject$
            .subscribe(newSettings => group.settings = newSettings);

        if (!this.tabs[tabId]) {
            this.tabs[tabId] = {};
        }

        this.tabs[tabId][groupId] = group;

        return group;
    }
}
